/**
 * Commissioning a Project (ADR-0024 step 4, YAG-338 wave 3).
 *
 * A sibling of `useResolveWorkItemGate`, and separate from it on purpose. Every
 * other gate kind resolves through `POST /api/work-items/:id/gates/:gateId/
 * resolve`; `commission_project` does not, because resolving the gate and
 * commissioning the Project are two writes to two tables that only mean anything
 * together, and the one transaction that does both lives behind
 * `POST /api/projects/:id/commission`. The gate route deliberately has no
 * `commission_project` branch: a second door into an atomic pair is a second
 * chance to land half of it.
 *
 * The request body carries only the gate id. The first milestone's work items
 * are sourced server-side from the gate's own payload, so no surface has to
 * remember (or be trusted about) a ticket list it was handed turns earlier.
 *
 * Mirrors `useResolveWorkItemGate`'s shape exactly — `{ fn, isPending, error }`,
 * throwing on failure after setting `error` — so a caller can swap which verb it
 * is offering without changing how it handles the result.
 */
import { useCallback, useState } from "react";
import type { Project, ProjectPushReport, WorkItemGate } from "@yagni/shared";
import { readJsonError } from "../lib/readJsonError.js";

export interface CommissionProjectInput {
  projectId: string;
  gateId: string;
}

export interface CommissionProjectResult {
  project: Project;
  gate: WorkItemGate;
  /**
   * The brief revision the resolution pinned, or null when it resolved
   * unpinned. Reported for the receipt and read by NOTHING that decides: ADR-0024
   * is explicit that "a resolution with no pin is a legal resolution".
   */
  pinnedRevisionId: string | null;
  /** How many of the first milestone's work items the commissioning linked. */
  linkedCount: number;
}

export function useCommissionProject(): {
  commission: (input: CommissionProjectInput) => Promise<CommissionProjectResult>;
  isCommissioning: boolean;
  error: Error | null;
} {
  const [isCommissioning, setIsCommissioning] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const commission = useCallback(
    async (input: CommissionProjectInput): Promise<CommissionProjectResult> => {
      setIsCommissioning(true);
      setError(null);
      try {
        const res = await fetch(
          `/api/projects/${encodeURIComponent(input.projectId)}/commission`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ gateId: input.gateId }),
          },
        );
        if (!res.ok) {
          // The server's message is the honest one here: the Goal-redirect cop
          // refuses an outcome-shaped ask at this gate with a sentence that
          // explains where the ask belongs instead, and a generic
          // "Commissioning failed" would throw that away.
          throw new Error(await readJsonError(res));
        }
        return (await res.json()) as CommissionProjectResult;
      } catch (err) {
        const nextError =
          err instanceof Error ? err : new Error("Commissioning failed");
        setError(nextError);
        throw nextError;
      } finally {
        setIsCommissioning(false);
      }
    },
    [],
  );

  return { commission, isCommissioning, error };
}

/**
 * The SECOND confirm (ADR-0024 step 5, route built in wave 4).
 *
 * `POST /api/projects/:id/milestones/:mid/push` RESOLVES the gate and THEN
 * pushes, in that order, which is why this must not go through the generic gate
 * resolver even though a `milestone_decomposition` gate would technically accept
 * one. A generic resolve leaves a confirmed gate and no push. It degrades safely
 * (a later call to the push route finds the gate already resolved and pushes
 * anyway), but "safe to recover from" is not the same as "what the button should
 * do", and the person who pressed it is owed the thing they pressed it for.
 *
 * This is the only moment in the whole Project flow when anything leaves YAGNI,
 * so it is also the only place the copy is allowed to say a tracker is written.
 */
export interface PushMilestoneInput {
  projectId: string;
  milestoneId: string;
  gateId: string;
}

/**
 * The push route answers with wave 4's `ProjectPushReport`, verbatim.
 *
 * Reported rather than reshaped, because `failures` is the half a surface must
 * not quietly drop: a push can accept eight tickets and refuse the ninth, and a
 * hook that returned only a count would turn a partial push into a clean one.
 */
export type PushMilestoneResult = ProjectPushReport;

export function usePushMilestone(): {
  push: (input: PushMilestoneInput) => Promise<PushMilestoneResult>;
  isPushing: boolean;
  error: Error | null;
} {
  const [isPushing, setIsPushing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const push = useCallback(
    async (input: PushMilestoneInput): Promise<PushMilestoneResult> => {
      setIsPushing(true);
      setError(null);
      try {
        const res = await fetch(
          `/api/projects/${encodeURIComponent(input.projectId)}/milestones/${encodeURIComponent(
            input.milestoneId,
          )}/push`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ gateId: input.gateId }),
          },
        );
        if (!res.ok) throw new Error(await readJsonError(res));
        return (await res.json()) as PushMilestoneResult;
      } catch (err) {
        const nextError = err instanceof Error ? err : new Error("Push failed");
        setError(nextError);
        throw nextError;
      } finally {
        setIsPushing(false);
      }
    },
    [],
  );

  return { push, isPushing, error };
}
